import { ICountry } from "../types/country";

// This should be identical to PAYMENT_CURRENCIES in alpha/utils.py, with the exception of BGN which we've temporarily removed on the FE
// per https://linear.app/levro/issue/ENG-391/temporarily-deprecate-bgn-on-the-fe
export const SUPPORTED_CURRENCIES = [
    "EUR",
    "PLN",
    "JPY",
    "GBP",
    "DKK",
    "KWD",
    "SEK",
    "SAR",
    "CNY",
    "CAD",
    "SGD",
    "OMR",
    "QAR",
    "UGX",
    "BHD",
    "NZD",
    "HKD",
    "MXN",
    "NOK",
    "RON",
    "THB",
    "HUF",
    "AED",
    "TRY",
    "CZK",
    "ZAR",
    "AUD",
    "CHF",
    "KES",
    "ILS",
    "PHP",
    "INR",
    "IDR",
    "MYR",
    "USD",
] as const;

type SupportedCurrency = (typeof SUPPORTED_CURRENCIES)[number];

// From: https://en.wikipedia.org/wiki/List_of_circulating_currencies
const CURRENCIES_NAMES: Record<SupportedCurrency, string> = {
    EUR: "Euro",
    PLN: "Polish złoty",
    JPY: "Japanese yen",
    GBP: "Great Britain Pound",
    DKK: "Danish krone",
    KWD: "Kuwaiti dinar",
    SEK: "Swedish krona",
    SAR: "Saudi riyal",
    CNY: "Chinese yuan",
    CAD: "Canadian dollar",
    SGD: "Singapore dollar",
    OMR: "Omani rial",
    QAR: "Qatari riyal",
    UGX: "Ugandan shilling",
    BHD: "Bahraini dinar",
    NZD: "New Zealand dollar",
    HKD: "Hong Kong dollar",
    MXN: "Mexican peso",
    NOK: "Norwegian krone",
    RON: "Romanian leu",
    THB: "Thai baht",
    HUF: "Hungarian forint",
    AED: "United Arab Emirates dirham",
    TRY: "Turkish lira",
    CZK: "Czech koruna",
    ZAR: "South African rand",
    AUD: "Australian dollar",
    CHF: "Swiss franc",
    KES: "Kenyan shilling",
    ILS: "Israeli new shekel",
    PHP: "Philippine peso",
    INR: "Indian rupee",
    IDR: "Indonesian rupiah",
    MYR: "Malaysian ringgit",
    USD: "United States dollar",
};

export function getCurrencyName(c: string): string | undefined {
    return CURRENCIES_NAMES[c as SupportedCurrency];
}

const PAYOUT_CURRENCIES: Set<string> = new Set(["IDR", "INR", "MYR", "PHP"]);
const CONVERT_ONLY_CURRENCIES: Set<string> = new Set(["MXN"]);

export function isPayoutOnlyCurrency(c: string): boolean {
    return PAYOUT_CURRENCIES.has(c);
}

export const PAYMENT_CURRENCIES = SUPPORTED_CURRENCIES.filter((c) => !CONVERT_ONLY_CURRENCIES.has(c));
// Currencies that we support converting: the sell only countries have restrictions
export const CONVERSION_CURRENCIES = SUPPORTED_CURRENCIES.filter((c) => !isPayoutOnlyCurrency(c));
export const UNRESTRICTED_PAYMENT_CURRENCIES = PAYMENT_CURRENCIES.filter((c) => !isPayoutOnlyCurrency(c));

export const MULTIPLE_ACCOUNT_CURRENCIES: string[] = ["USD"];

const EMPTY_ARRAY: [] = [];
export function getCountryOptions(
    countryByCountryCode: { [countryCode: string]: ICountry } | undefined,
): Array<{ label: string; value: string }> {
    if (countryByCountryCode == null) {
        return EMPTY_ARRAY;
    }
    return Object.values(countryByCountryCode).map((c) => ({ label: c.name, value: c.countryCode }));
}

export function getCurrencyForCountryCode(
    countryByCountryCode: { [countryCode: string]: ICountry } | undefined,
    countryCode: string | undefined,
): string | undefined {
    if (countryCode == null || countryByCountryCode == null) {
        return undefined;
    }
    return countryByCountryCode[countryCode]?.currency ?? undefined;
}

export function getCountry(
    countryByCountryCode: { [countryCode: string]: ICountry } | undefined,
    countryCode: string | undefined,
): ICountry | undefined {
    if (countryCode == null || countryByCountryCode == null) {
        return undefined;
    }
    const sanitizedCountryCode = countryCode === "EUROPEAN_UNION" ? "ES" : countryCode;
    return countryByCountryCode[sanitizedCountryCode];
}
